import { useCallback, useEffect, useRef, useState } from 'react';
import { posApi } from '../api/endpoints';
import { onReconnect, useConnectivity, type ConnectionState } from '../lib/connectivity';
import { pairingApi } from '../api/stations';
import { ApiClientError } from '../api/client';
import { messageFor } from '../api/messages';
import { whenBackgroundIdle } from './backgroundSend';
import { CatalogIndex } from './catalogIndex';
import { tryAcquireRegisterLock } from './db';
import { clientDeviceId } from './deviceId';
import { currentPerson } from '../auth/currentPerson';
import { forgetLocalRegister, rememberPosEvent } from './sessions';
import { SyncScheduler, type SchedulerStatus } from './scheduler';
import { discardOldCursor, persistQuietly, readStand, type StandInfo } from './standState';
import { HANDOVER_FLOOR_KEY, HANDOVER_KEY, PosStore } from './store';
import { SyncEngine } from './sync';
import { createSyncApi } from './syncApi';
import type { LocalSale, RegisterMeta } from './types';

export type PosPhase =
  | { kind: 'loading' }
  | { kind: 'locked' }
  | { kind: 'setup' }
  /** Die Kasse wurde an diesem Gerät übergeben: Die nächste Person übernimmt sie mit ihrem Konto (`takeOver`). */
  | { kind: 'handover' }
  /**
   * Die Kasse auf diesem Gerät gehört einer anderen Person als der, die jetzt angemeldet ist. Es wird weder kassiert noch
   * gesendet (die wartenden Vorgänge gehen unter dem Konto der Besitzerin hinaus, wenn sie sich anmeldet). `waiting`: Zahl der
   * wartenden Verkäufe und Stornos.
   */
  | { kind: 'foreign'; waiting: number }
  | { kind: 'ready' }
  | { kind: 'failed'; message: string };

export interface PendingCounts {
  sales: number;
  voids: number;
  failed: number;
}

/** Seit „Verstanden“ aufgelaufene Auffälligkeiten aus dem Sync (nur im Speicher der Seite). */
export interface UnseenIssues {
  /** Artikel, die schon an einer anderen Kasse verkauft waren (Konfliktzeilen des Servers). */
  conflicts: number;
  /** Vom Server abgelehnte Verkäufe (nicht übertragbar). */
  rejected: number;
}

const NO_ISSUES: UnseenIssues = { conflicts: 0, rejected: 0 };

export interface PosHandle {
  phase: PosPhase;
  store: PosStore | null;
  register: RegisterMeta | null;
  status: SchedulerStatus | null;
  pending: PendingCounts;
  history: LocalSale[];
  /** Stornos, die der Server noch nicht bestätigt hat, je Verkauf (Sendestatus der Karte, U17e). */
  openVoids: ReadonlyMap<string, 'pending' | 'failed'>;
  /** Die Kasse wurde über einen Kassenplatz mit Schnellzugang gekoppelt (Geräte-Token, kein Konto, kein Zugriffstoken). */
  deviceMode: boolean;
  /** Das Gerät wurde abgekoppelt oder gesperrt: kein Verkauf mehr, die Warteschlange bleibt (Notausgang, Erneut versuchen). */
  revoked: boolean;
  /** Verbindungszustand aus echten Anfragen (U15a), nicht aus `navigator.onLine` (das lügt im WLAN ohne Internet). */
  connection: ConnectionState;
  /** Konflikte und Abgelehnte aus den Sync-Läufen, die noch niemand bestätigt hat. */
  unseenIssues: UnseenIssues;
  /** „Verstanden“: setzt `unseenIssues` zurück. */
  dismissIssues: () => void;
  /** Suchindex über den Artikelstand (U17b): lädt im Leerlauf und folgt den Seiten des Abzugs. */
  catalog: CatalogIndex;
  /** Artikelstand auf diesem Gerät (Zeitpunkt, Zahl); `null`, bis der Speicher gelesen ist (U17c). */
  stand: StandInfo | null;
  /** Zählt bei jeder Änderung an Speicher oder Sync hoch (die Oberfläche lädt dann Verlauf und Zähler neu). */
  version: number;
  refresh: () => void;
  syncNow: () => void;
  /** Nach „abgekoppelt“ erneut versuchen (z. B. wenn die Leitung „Nur noch hochladen“ erlaubt hat). */
  retryUpload: () => void;
  /** Einen eingerichteten, freien Kassenplatz wählen (Kassenmodell E14-2) und das Gerät daran koppeln. */
  openStation: (stationId: string) => Promise<void>;
  /**
   * Kasse übergeben (Schichtwechsel): Gibt die Kasse auf dem Server frei, nur wenn alle Verkäufe übertragen sind, und legt den
   * Einmal-Code für die Übernahme ab (keine Sitzung der bisherigen Person bleibt). Danach kassiert das Gerät nicht mehr, bis
   * jemand übernimmt. Wirft eine verständliche Meldung, wenn noch etwas offen ist.
   */
  prepareHandover: () => Promise<void>;
  /** Die übergebene Kasse mit dem eigenen Konto übernehmen: neue Sitzung, die Kasse bleibt dieselbe. */
  takeOver: () => Promise<void>;
  /**
   * Übergabe zurücknehmen: Nur die bisherige Person (der die Kasse auf dem Server noch gehört) holt sich eine neue Sitzung und
   * arbeitet weiter; der Einmal-Code verfällt damit. Für alle anderen wirft es eine verständliche Meldung.
   */
  cancelHandover: () => Promise<void>;
  /**
   * Fremde Kasse (`foreign`) vom Gerät entfernen, damit die angemeldete Person ihre eigene einrichtet. Nur, wenn nichts mehr
   * wartet: sonst wirft es eine verständliche Meldung und nichts wird gelöscht.
   */
  discardForeign: () => Promise<void>;
}

const EMPTY: PendingCounts = { sales: 0, voids: 0, failed: 0 };

/** Abstand der neuen Versuche, solange die Sperre der Kasse belegt ist. */
const LOCK_RETRY_MS = 2_000;

/** Verständliche Ablehnung der Kasse selbst (kein technischer Fehler): Die Oberfläche zeigt den Text so an. */
export class PosNotice extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PosNotice';
  }
}

/**
 * Öffnet den lokalen Speicher der Kasse dieses Basars, sichert den einen aktiven Tab (POS-07) und steuert den Sync. Alles
 * Geld Betreffende liegt im Speicher, nicht im React-Zustand: Ein Neuladen der Seite verliert nichts.
 */
export function usePos(eventId: string): PosHandle {
  const [phase, setPhase] = useState<PosPhase>({ kind: 'loading' });
  const [register, setRegister] = useState<RegisterMeta | null>(null);
  const [status, setStatus] = useState<SchedulerStatus | null>(null);
  const [pending, setPending] = useState<PendingCounts>(EMPTY);
  const [history, setHistory] = useState<LocalSale[]>([]);
  const [openVoids, setOpenVoids] = useState<ReadonlyMap<string, 'pending' | 'failed'>>(
    () => new Map(),
  );
  const [version, setVersion] = useState(0);
  const [unseenIssues, setUnseenIssues] = useState<UnseenIssues>(NO_ISSUES);
  const connection = useConnectivity();
  const lastSummary = useRef<SchedulerStatus['lastSummary']>(null);
  const store = useRef<PosStore | null>(null);
  const scheduler = useRef<SyncScheduler | null>(null);
  const [ready, setReady] = useState<PosStore | null>(null);
  const [catalog] = useState(() => new CatalogIndex());
  const [stand, setStand] = useState<StandInfo | null>(null);

  const refresh = useCallback(() => {
    setVersion((n) => n + 1);
  }, []);

  const startSync = useCallback(
    (opened: PosStore, initialRevoked = false) => {
      // Bei einer gekoppelten Kasse heißt 404 beim Sync: abgekoppelt oder gesperrt (siehe `createSyncApi`)
      // Jede Seite des Abzugs geht auch an den Suchindex (ohne Neuladen), und zwar so, wie sie gespeichert wurde: Lokal
      // verkaufte, noch nicht gesendete Artikel bleiben dort „verkauft“
      const engine = new SyncEngine(opened, createSyncApi(eventId, opened), undefined, (items) => {
        catalog.apply(items);
      });
      // „Abgekoppelt“ überlebt ein Neuladen: Der Zustand liegt im Speicher und gilt, bis ein Sync wieder gelingt
      const persisted = { revoked: initialRevoked };
      const created = new SyncScheduler(engine, {
        initialRevoked,
        onStatus: (next) => {
          const revokedNow = next.revoked === true;
          if (revokedNow !== persisted.revoked) {
            persisted.revoked = revokedNow;
            opened.setMeta('revoked', revokedNow).catch(() => undefined);
          }
          // Jeder Lauf bringt eine neue Zusammenfassung: Konflikte und Abgelehnte werden aufsummiert, bis jemand bestätigt
          const summary = next.lastSummary;
          if (summary !== null && summary !== lastSummary.current) {
            lastSummary.current = summary;
            if (summary.conflicts > 0 || summary.rejected > 0) {
              setUnseenIssues((before) => ({
                conflicts: before.conflicts + summary.conflicts,
                rejected: before.rejected + summary.rejected,
              }));
            }
          }
          setStatus(next);
          setVersion((n) => n + 1);
        },
      });
      scheduler.current = created;
      created.start();
    },
    [eventId, catalog],
  );

  useEffect(() => {
    const guard = { cancelled: false, release: null as (() => void) | null };
    // Funktion statt Feldzugriff: Nach dem ersten Vergleich hielte TypeScript den Wert sonst für fest false
    const isCancelled = (): boolean => guard.cancelled;
    void (async () => {
      try {
        // Ein kurzer Hintergrund-Lauf dieses Fensters hält die Sperre noch: erst abwarten, sonst wirkt es wie ein zweiter Tab
        await whenBackgroundIdle(eventId);
        let release = await tryAcquireRegisterLock(eventId);
        // Die Sperre hält ein anderer Tab, oder nur kurz der Hintergrund-Sender eines anderen Fensters (U15a): Hinweis zeigen
        // und alle 2 s erneut versuchen, statt bis zum Neuladen gesperrt zu bleiben. Immer nur ein Halter, also nie doppelt.
        while (release === null) {
          if (isCancelled()) return;
          setPhase({ kind: 'locked' });
          await new Promise((resolve) => setTimeout(resolve, LOCK_RETRY_MS));
          if (isCancelled()) return;
          await whenBackgroundIdle(eventId);
          release = await tryAcquireRegisterLock(eventId);
        }
        if (isCancelled()) {
          release();
          return;
        }
        setPhase({ kind: 'loading' });
        guard.release = release;
        const opened = await PosStore.open(`barso-pos-${eventId}`);
        if (isCancelled()) {
          opened.close();
          release();
          return;
        }
        store.current = opened;
        setReady(opened);
        const meta = await opened.register();
        if (isCancelled()) return;
        if (meta === undefined) {
          setPhase({ kind: 'setup' });
          return;
        }
        // Eine Kasse mit Konto gehört einer Person: Eine andere Person am Gerät kassiert hier nicht und sendet nichts
        const person = meta.mode === 'name' ? null : currentPerson();
        if (person !== null && meta.ownerUserId === undefined) {
          // Ältere Kasse ohne vermerkte Besitzerin: Ohne wartende Vorgänge gehört sie der Person, die sie zuerst wieder öffnet.
          // Warten noch Verkäufe, Stornos, Kommentare oder Fotos, gilt nur der Beweis des Servers (er erneuert die Sitzung nur
          // für die Besitzerin); ohne ihn bleibt sie ohne Besitzerin und gesperrt, statt unter irgendeinem Konto hinauszugehen.
          const counts = await opened.pending();
          const waiting =
            counts.sales + counts.voids + counts.failed + (await opened.pendingSideWork());
          if (waiting === 0) {
            await opened.setOwner(person);
            meta.ownerUserId = person;
          } else {
            try {
              const proof = await posApi.renewSession(eventId, meta.registerId);
              if (isCancelled()) return;
              await opened.setSession(proof.sessionToken, proof.sessionExpiresAt);
              await opened.setOwner(person);
              meta.ownerUserId = person;
              meta.sessionToken = proof.sessionToken;
              meta.sessionExpiresAt = proof.sessionExpiresAt;
            } catch {
              // Kein Beweis (offline, nicht die Besitzerin): die Kasse bleibt gesperrt, nichts geht verloren
            }
          }
        }
        setRegister(meta);
        const wasRevoked = (await opened.getMeta<boolean>('revoked')) === true;
        if (isCancelled()) return;
        // Übergeben (Schichtwechsel): Bis die nächste Person übernimmt, arbeitet die Kasse nicht und synchronisiert nicht
        if (
          meta.mode !== 'name' &&
          typeof (await opened.getMeta<unknown>(HANDOVER_KEY)) === 'string'
        ) {
          if (isCancelled()) return;
          setRegister(meta);
          setPhase({ kind: 'handover' });
          return;
        }
        if (isCancelled()) return;
        if (person !== null && meta.ownerUserId !== person) {
          const counts = await opened.pending();
          if (isCancelled()) return;
          setPhase({
            kind: 'foreign',
            waiting: counts.sales + counts.voids + counts.failed + (await opened.pendingSideWork()),
          });
          return;
        }
        setPhase({ kind: 'ready' });
        // Alter Stand (über 12 h): der erste Abgleich lädt ihn einmal vollständig neu; bis dahin gilt der alte (U17c)
        await discardOldCursor(opened, Date.now()).catch(() => false);
        if (isCancelled()) return;
        startSync(opened, wasRevoked);
        // Einmal still um dauerhaften Speicher bitten (nie in Firefox)
        void persistQuietly(opened).catch(() => false);
        rememberPosEvent(eventId, meta.mode === 'name');
        void renewSessionIfNeeded(opened, eventId, meta, (next) => {
          if (!isCancelled()) setRegister(next);
        });
      } catch (caught) {
        if (!isCancelled()) setPhase({ kind: 'failed', message: messageFor(caught) });
      }
    })();
    return () => {
      guard.cancelled = true;
      scheduler.current?.stop();
      scheduler.current = null;
      store.current?.close();
      store.current = null;
      guard.release?.();
    };
  }, [eventId, startSync]);

  // Suchindex einmal im Leerlauf laden (U17b)
  useEffect(() => {
    if (ready !== null) catalog.loadWhenIdle(ready);
  }, [ready, catalog]);

  useEffect(() => {
    if (ready === null) return;
    const guard = { cancelled: false };
    void (async () => {
      try {
        const [counts, recent, current, voids] = await Promise.all([
          ready.pending(),
          ready.history(10),
          readStand(ready),
          ready.openVoids(),
        ]);
        if (guard.cancelled) return;
        setPending(counts);
        setHistory(recent);
        setOpenVoids(voids);
        setStand(current);
      } catch {
        // Speicher inzwischen geschlossen (Kasse verlassen): nichts mehr anzuzeigen
      }
    })();
    return () => {
      guard.cancelled = true;
    };
  }, [ready, version]);

  const openStation = useCallback(
    async (stationId: string) => {
      const opened = store.current;
      if (opened === null) throw new Error('Die Kasse ist nicht geöffnet.');
      const paired = await pairingApi.openStation(eventId, stationId, clientDeviceId());
      const meta: RegisterMeta = {
        eventId,
        registerId: paired.id,
        label: paired.label,
        labelSalt: 0,
        sessionToken: paired.sessionToken,
        sessionExpiresAt: paired.sessionExpiresAt,
        mode: paired.mode,
        stationId: paired.stationId,
        ...ownerOf(paired.mode),
      };
      rememberPosEvent(eventId);
      await opened.setRegister(meta);
      await opened.setMeta('revoked', false);
      setRegister(meta);
      setPhase({ kind: 'ready' });
      startSync(opened);
    },
    [eventId, startSync],
  );

  const prepareHandover = useCallback(async () => {
    const opened = store.current;
    const meta = register;
    if (opened === null || meta === null || meta.mode === 'name') {
      throw new PosNotice('Diese Kasse lässt sich nicht übergeben.');
    }
    const counts = await opened.pending();
    if (counts.sales + counts.voids + counts.failed + (await opened.pendingSideWork()) > 0) {
      throw new PosNotice(
        'Es sind noch Verkäufe nicht übertragen. Bitte zuerst mit Netz übertragen, sonst würden sie der nächsten Person zugerechnet.',
      );
    }
    // Der Server prüft dieselbe Bedingung an der höchsten vergebenen Nummer (Verkäufe, die nur hier liegen, blockieren)
    const { lastSeq } = await opened.counters();
    const released = await posApi.releaseRegister(eventId, meta.registerId, lastSeq);
    // Ab hier kassiert das Gerät nicht mehr: Bis zur Übernahme liegt nur der Einmal-Code bereit, keine Sitzung der Person
    scheduler.current?.stop();
    scheduler.current = null;
    await opened.setMeta(HANDOVER_KEY, released.handoverToken);
    await opened.setSession(undefined);
    setRegister({ ...meta, sessionToken: undefined, sessionExpiresAt: undefined });
    setPhase({ kind: 'handover' });
  }, [eventId, register]);

  const takeOver = useCallback(async () => {
    const opened = store.current;
    if (opened === null) throw new PosNotice('Die Kasse ist nicht geöffnet.');
    const meta = await opened.register();
    const token = await opened.getMeta<unknown>(HANDOVER_KEY);
    if (meta === undefined || typeof token !== 'string') {
      throw new PosNotice('Diese Kasse ist nicht zur Übergabe vorgemerkt.');
    }
    const { lastSeq } = await opened.counters();
    const next = await posApi.handover(eventId, meta.registerId, token, lastSeq);
    await opened.setSession(next.sessionToken, next.sessionExpiresAt);
    // Frühere Verkäufe bleiben bei der vorigen Person: hier nicht mehr stornierbar (der Server lehnt es ab)
    await opened.setMeta(HANDOVER_FLOOR_KEY, lastSeq);
    await opened.setMeta(HANDOVER_KEY, null);
    // Die Kasse gehört jetzt der übernehmenden Person (der Server hat sie ihr zugeordnet)
    const owner = currentPerson();
    if (owner !== null) await opened.setOwner(owner);
    const updated: RegisterMeta = {
      ...meta,
      ...(owner === null ? {} : { ownerUserId: owner }),
      sessionToken: next.sessionToken,
      sessionExpiresAt: next.sessionExpiresAt,
    };
    rememberPosEvent(eventId);
    setRegister(updated);
    setPhase({ kind: 'ready' });
    startSync(opened, (await opened.getMeta<boolean>('revoked')) === true);
  }, [eventId, startSync]);

  const cancelHandover = useCallback(async () => {
    const opened = store.current;
    if (opened === null) throw new PosNotice('Die Kasse ist nicht geöffnet.');
    const meta = await opened.register();
    if (meta === undefined) throw new PosNotice('Diese Kasse ist nicht zur Übergabe vorgemerkt.');
    let renewed: { sessionToken: string; sessionExpiresAt: string };
    try {
      // Der Server erneuert nur für die Person, der die Kasse gehört, und verwirft dabei die Freigabe
      renewed = await posApi.renewSession(eventId, meta.registerId);
    } catch (caught) {
      if (caught instanceof ApiClientError && caught.status === 404) {
        throw new PosNotice(
          'Zurücknehmen kann nur die Person, die die Kasse übergeben hat. Bitte mit „Kasse übernehmen“ weitermachen.',
        );
      }
      throw caught;
    }
    await opened.setSession(renewed.sessionToken, renewed.sessionExpiresAt);
    await opened.setMeta(HANDOVER_KEY, null);
    // Der Server hat nur für die Besitzerin erneuert: Das ist der Beweis, wem die Kasse gehört
    const owner = currentPerson();
    if (owner !== null) await opened.setOwner(owner);
    rememberPosEvent(eventId);
    setRegister({
      ...meta,
      ...(owner === null ? {} : { ownerUserId: owner }),
      sessionToken: renewed.sessionToken,
      sessionExpiresAt: renewed.sessionExpiresAt,
    });
    setPhase({ kind: 'ready' });
    startSync(opened, (await opened.getMeta<boolean>('revoked')) === true);
  }, [eventId, startSync]);

  const discardForeign = useCallback(async () => {
    const opened = store.current;
    if (opened === null) throw new PosNotice('Die Kasse ist nicht geöffnet.');
    const counts = await opened.pending();
    if (counts.sales + counts.voids + counts.failed + (await opened.pendingSideWork()) > 0) {
      throw new PosNotice(
        'Auf dieser Kasse warten noch Verkäufe. Sie gehen unter dem Konto der Person hinaus, der die Kasse gehört: Bitte dort anmelden.',
      );
    }
    // Der Speicher muss für das Löschen geschlossen sein; schlägt es fehl, wird er wieder geöffnet (nichts geht verloren)
    opened.close();
    try {
      await forgetLocalRegister(eventId);
    } catch (caught) {
      const again = await PosStore.open(`barso-pos-${eventId}`);
      store.current = again;
      setReady(again);
      throw caught;
    }
    const fresh = await PosStore.open(`barso-pos-${eventId}`);
    store.current = fresh;
    setReady(fresh);
    setRegister(null);
    setPhase({ kind: 'setup' });
  }, [eventId]);

  // Netz zurück, Seite sichtbar, App fortgesetzt: sofort senden, nicht erst beim nächsten Backoff-Termin (bis zu 75 s)
  useEffect(
    () =>
      onReconnect(() => {
        scheduler.current?.nudge();
      }),
    [],
  );

  const dismissIssues = useCallback(() => {
    setUnseenIssues(NO_ISSUES);
  }, []);

  const syncNow = useCallback(() => {
    void scheduler.current?.trigger();
  }, []);

  const retryUpload = useCallback(() => {
    scheduler.current?.retry();
  }, []);

  return {
    phase,
    store: ready,
    register,
    status,
    pending,
    history,
    openVoids,
    catalog,
    stand,
    deviceMode: register?.mode === 'name',
    revoked: status?.revoked === true,
    connection,
    unseenIssues,
    dismissIssues,
    version,
    refresh,
    syncNow,
    retryUpload,
    openStation,
    prepareHandover,
    takeOver,
    cancelHandover,
    discardForeign,
  };
}

/** Besitzerin einer neu gekoppelten Kasse: die angemeldete Person (nur bei Kassen mit Konto). */
function ownerOf(mode: 'login' | 'name' | undefined): { ownerUserId?: string } {
  const person = mode === 'name' ? null : currentPerson();
  return person === null ? {} : { ownerUserId: person };
}

/** So kurz vor dem Ablauf (oder ohne Sitzung, z. B. nach einer Abmeldung) wird eine neue Sitzung geholt. */
const RENEW_BEFORE_MS = 24 * 60 * 60 * 1000;

/**
 * Holt still im Hintergrund eine neue Basar-Sitzung (SYN-10), wenn sie fehlt oder bald abläuft. Das braucht Netz und die
 * Anmeldung; ohne beides passiert nichts, die Kasse arbeitet mit dem Zugriffstoken und der Warteschlange weiter.
 */
async function renewSessionIfNeeded(
  store: PosStore,
  eventId: string,
  meta: RegisterMeta,
  onRenewed: (meta: RegisterMeta) => void,
): Promise<void> {
  // Schnellzugang: Es gibt kein Konto und kein Zugriffstoken, also keine Erneuerung (E11)
  if (meta.mode === 'name') return;
  const expires =
    meta.sessionExpiresAt === undefined ? Number.NaN : new Date(meta.sessionExpiresAt).getTime();
  const fresh =
    meta.sessionToken !== undefined &&
    !Number.isNaN(expires) &&
    expires - Date.now() > RENEW_BEFORE_MS;
  if (fresh) return;
  try {
    const renewed = await posApi.renewSession(eventId, meta.registerId);
    await store.setSession(renewed.sessionToken, renewed.sessionExpiresAt);
    onRenewed({
      ...meta,
      sessionToken: renewed.sessionToken,
      sessionExpiresAt: renewed.sessionExpiresAt,
    });
  } catch {
    // Offline, nicht angemeldet oder Kasse beendet: es bleibt beim Zugriffstoken
  }
}
